import React from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { Appearance } from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import { IconButton } from '@atlaskit/button/new';
import UploadIcon from '@atlaskit/icon/core/migration/upload--export';
import ExportIcon from '@atlaskit/icon/glyph/export';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import messages from './messages';
import { useCsvExporter } from './utils/use-exporter.tsx';

type ExportButtonProps = {
	appearance?: Appearance;
	isIconOnly?: boolean;
};

const ExportButton = ({ appearance = 'default', isIconOnly = false }: ExportButtonProps) => {
	const { formatMessage } = useIntl();
	const exporter = useCsvExporter();

	if (exporter.status === 'failed') {
		throw exporter.error;
	}

	return isVisualRefreshEnabled() ? (
		<IconButton
			label={formatMessage(messages.exportButtonLabel)}
			isLoading={exporter.status === 'exporting'}
			isDisabled={exporter.status === 'initializing'}
			onClick={(event, analyticsEvent: UIAnalyticsEvent) => {
				// @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"initializing" | "exporting"' and '"failed"' have no overlap.
				if (exporter.status === 'ready' || exporter.status === 'failed') {
					fireUIAnalytics(analyticsEvent, 'exportDeploymentsCsv');
					exporter.beginExport();
				}
			}}
			icon={UploadIcon}
			isTooltipDisabled={false}
		/>
	) : (
		<LoadingButton
			appearance={appearance}
			isLoading={exporter.status === 'exporting'}
			isDisabled={exporter.status === 'initializing'}
			onClick={(event, analyticsEvent: UIAnalyticsEvent) => {
				// @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"initializing" | "exporting"' and '"failed"' have no overlap.
				if (exporter.status === 'ready' || exporter.status === 'failed') {
					fireUIAnalytics(analyticsEvent, 'exportDeploymentsCsv');
					exporter.beginExport();
				}
			}}
			iconBefore={<ExportIcon label="" />}
		>
			{!isIconOnly && formatMessage(messages.exportButtonLabel)}
		</LoadingButton>
	);
};

export default ExportButton;
